import { Layout, Segmented, Select, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Spinner, unwrapError, useApiClient } from '@clh/ui';

import { useToast } from '../hooks/use-toast';
import { Content, Header } from '../layout';

import { PayPeriodContext } from './pay-period-context';
import {
    findRequestedPayPeriodKey,
    payPeriodKey,
} from './pay-period-selection';

interface PayPeriod {
    startDate: Date;
    endDate: Date;
}

const { Text } = Typography;

export default function PayrollInvoicing() {
    const api = useApiClient();
    const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
    const [selectedPayPeriod, setSelectedPayPeriod] = useState<string | null>(
        null
    );
    const [loading, setLoading] = useState(false);
    const { notification } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const requestedPayPeriodStart = searchParams.get('payPeriodStart');

    const currentTab = location.pathname.split('/').pop();

    const tabs = [
        { label: 'Payroll', value: 'paychecks' },
        { label: 'Invoices', value: 'invoices' },
        { label: 'Shifts', value: 'shifts' },
    ];

    useEffect(() => {
        if (!api) return;

        const fetchPayPeriods = async () => {
            setLoading(true);
            try {
                const periods = await api.payPeriodControllerGetAllPayPeriods();

                setPayPeriods(periods);

                const requestedKey = findRequestedPayPeriodKey(
                    periods,
                    requestedPayPeriodStart
                );
                if (requestedKey) {
                    setSelectedPayPeriod(requestedKey);
                    return;
                }

                // Set default to most recent completed pay period (first pay period whose end date is before today)
                if (periods.length > 0) {
                    const now = new Date();
                    const completedPeriod = periods.find(
                        (period) => new Date(period.endDate) < now
                    );

                    if (completedPeriod) {
                        setSelectedPayPeriod(payPeriodKey(completedPeriod));
                    } else {
                        // If no completed periods found, default to the first one
                        setSelectedPayPeriod(payPeriodKey(periods[0]));
                    }
                }
            } catch (error) {
                notification.error({
                    message: unwrapError(error),
                    duration: 3,
                });
            } finally {
                setLoading(false);
            }
        };

        void fetchPayPeriods();
    }, [api]);

    if (!api) {
        return <Spinner />;
    }

    const payPeriodOptions = payPeriods.map((period) => ({
        label: `${period.startDate.toLocaleDateString('en-US', {
            timeZone: 'UTC',
        })} - ${period.endDate.toLocaleDateString('en-US', {
            timeZone: 'UTC',
        })}`,
        value: payPeriodKey(period),
    }));

    return (
        <Layout>
            <Header>
                <h2>Payroll & Invoicing</h2>
            </Header>
            <Content>
                <Space
                    direction="vertical"
                    style={{ display: 'flex', paddingBottom: 10 }}
                >
                    <Text>Pay Period:</Text>
                    <Select
                        value={selectedPayPeriod}
                        disabled={!payPeriodOptions.length}
                        onChange={setSelectedPayPeriod}
                        loading={loading}
                        options={payPeriodOptions}
                        virtual={false}
                        placeholder="Select pay period"
                    />
                    <Segmented
                        options={tabs}
                        value={currentTab}
                        onChange={(tab) => navigate(`/payroll/${tab}`)}
                    ></Segmented>
                </Space>
                {selectedPayPeriod ? (
                    <PayPeriodContext.Provider
                        value={{
                            startDate: new Date(
                                selectedPayPeriod.split('|')[0]
                            ),
                            endDate: new Date(selectedPayPeriod.split('|')[1]),
                        }}
                    >
                        <Outlet />
                    </PayPeriodContext.Provider>
                ) : null}
            </Content>
        </Layout>
    );
}
